import { RefObject, useEffect } from 'react'

/**
 * Появление блоков при прокрутке.
 *
 * Внутри `containerRef` ищет элементы с атрибутом `data-reveal` и показывает
 * каждый один раз, когда он входит в экран (стили — core/styles/reveal.scss).
 * Очерёдность внутри группы задаётся переменной `--reveal-delay` на элементе.
 *
 * Прячем только то, что ниже первого экрана: видимое не мигает, а без
 * IntersectionObserver (и в готовом HTML от prerender) страница остаётся
 * видимой целиком.  При «уменьшить движение» ничего не прячем вовсе.
 *
 * `deps` — когда пересобрать список (например, смена языка меняет разметку).
 */
export const useRevealOnScroll = (
    containerRef: RefObject<HTMLElement>,
    deps: ReadonlyArray<unknown> = []
) => {
    useEffect(() => {
        const container = containerRef.current
        if (!container || typeof IntersectionObserver === 'undefined') return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return
        }

        const targets = Array.from(
            container.querySelectorAll<HTMLElement>('[data-reveal]')
        ).filter(
            el =>
                !el.classList.contains('visible') &&
                el.getBoundingClientRect().top > window.innerHeight
        )

        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) return
                    entry.target.classList.add('visible')
                    observer.unobserve(entry.target)
                })
            },
            // threshold 0: высокие блоки (на телефоне — в несколько экранов)
            // не должны ждать, пока в кадр войдёт их заметная доля.
            { threshold: 0, rootMargin: '0px 0px -8% 0px' }
        )

        targets.forEach(el => {
            el.classList.add('reveal')
            observer.observe(el)
        })

        return () => {
            observer.disconnect()
            targets.forEach(el => el.classList.remove('reveal'))
        }
    }, deps)
}
